import React, { useMemo } from 'react';
import { ArrowLeft, Bot, Brain, Clock, Pencil, Settings2, Wrench } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../Context/authContext';
import agentService from '../../services/agentService';
import toolsService from '../../services/toolsService';
import memoryService from '../../services/memoryService';
import { formatDate } from '../../helpers/common';
import { ReponseMenu, StyleItem } from '../SideBar/SideBar.styles';
import Loaders from '../Loaders';
import { MenuBtn, ProfilePage } from './AgentProfile.style';

function resolveIconSrc(icon) {
  if (!icon || typeof icon !== 'string') return null;
  if (icon.startsWith('http://') || icon.startsWith('https://')) {
    return icon.split('?')[0].toLowerCase().endsWith('.svg') ? null : icon;
  }
  if (icon.startsWith('data:')) return icon;
  return null;
}

function normalizeIdList(value) {
  if (value == null || value === '') return [];
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[')) {
      try {
        return normalizeIdList(JSON.parse(trimmed));
      } catch {
        /* fall through */
      }
    }
    return trimmed
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }
  if (Array.isArray(value)) {
    return value.flatMap(item => {
      if (item == null || item === '') return [];
      if (typeof item === 'string' || typeof item === 'number') return [String(item)];
      if (typeof item === 'object') {
        const id = item.tool_id || item.id || item.value;
        return id != null && id !== '' ? [String(id)] : [];
      }
      return [];
    });
  }
  return [];
}

const AgentProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const detailQuery = useQuery({
    queryKey: ['get-single-agent', id],
    queryFn: () => agentService.getAgentDetail(id),
    enabled: !!id,
  });

  const detail = detailQuery.data?.detail;

  const { fetch } = useContextHook(AuthContext, v => ({
    fetch: v.fetch,
  }));

  const { _data: memoryData } = memoryService.GetMemoryList(id, fetch);
  const memories = memoryData?.results?.results || [];

  const userToolsQuery = useQuery({
    queryKey: ['tools', 'user_created'],
    queryFn: () => toolsService.getAllTools('user_created'),
    staleTime: Infinity,
  });

  const adminToolsQuery = useQuery({
    queryKey: ['tools', 'admin_created'],
    queryFn: () => toolsService.getAllTools('admin_created'),
    staleTime: Infinity,
  });

  const allTools = useMemo(
    () => [...(userToolsQuery.data?.tools || []), ...(adminToolsQuery.data?.tools || [])],
    [userToolsQuery.data, adminToolsQuery.data],
  );

  const toolIds = useMemo(() => new Set(normalizeIdList(detail?.tools)), [detail]);
  const agentTools = useMemo(
    () => allTools.filter(tool => toolIds.has(String(tool?.tool_id))),
    [allTools, toolIds],
  );

  if (detailQuery.isFetching) {
    return <Loaders pageLoader />;
  }

  const modelId = detail?.model_id || '';
  const provider = modelId.includes('gpt') ? 'OpenAI' : modelId.split('/')[0] || '—';
  const responseModel = modelId.includes('gpt') ? modelId : modelId.split('/')[1] || modelId || '—';
  const iconSrc = resolveIconSrc(detail?.icon);
  const memoryEnabled = detail?.enable_user_memories;

  return (
    <ProfilePage>
      <button type="button" className="back" onClick={() => navigate('/agents')}>
        <ArrowLeft size={16} /> Back to Agents
      </button>

      <div className="hero">
        <div className="avatar">
          {iconSrc ? <img src={iconSrc} alt={detail?.agent_name} /> : <Bot size={32} />}
        </div>
        <div className="hero-meta">
          <h1>{detail?.agent_name}</h1>
          {detail?.description && <p className="desc">{detail.description}</p>}
          <div className="id-row">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Clock size={14} /> Agent ID
            </span>
            <span className="mono">{id}</span>
          </div>
        </div>
        <button type="button" className="edit-btn" onClick={() => navigate(`/edit-agent/${id}`)}>
          <Pencil size={16} /> Edit
        </button>
      </div>

      <div className="grid">
        <div className="stack">
          <div className="card">
            <div className="card-head">
              <h3 className="title">
                <Settings2 size={20} />
                Instructions
              </h3>
            </div>
            <p
              className="instructions"
              dangerouslySetInnerHTML={{
                __html: detail?.instructions?.replace(/\r\n/g, '<br>') || 'No instructions provided.',
              }}
            />
          </div>

          {memoryEnabled && memories.length > 0 && (
            <div className="card">
              <div className="card-head">
                <h3 className="title">
                  <Brain size={20} />
                  Memory
                </h3>
                <span className="badge">{memories.length} items</span>
              </div>
              {memories.slice(0, 3).map((data, index) => (
                <div className="memory-item" key={index}>
                  <div>
                    <p className="text">{data?.memory}</p>
                    <p className="date">{formatDate(data?.created_at)}</p>
                  </div>
                  {data?.categories?.length > 0 && (
                    <div className="cats">
                      <span className="cat">{data.categories[0]}</span>
                      {data.categories.length > 1 && (
                        <ReponseMenu
                          menuButton={
                            <MenuBtn>
                              <span className="more">+ {data.categories.length - 1} more</span>
                            </MenuBtn>
                          }>
                          {data.categories.slice(1).map((item, i) => (
                            <StyleItem key={i}>
                              <div className="text">{item}</div>
                            </StyleItem>
                          ))}
                        </ReponseMenu>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="stack">
          <div className="card">
            <div className="card-head">
              <h3 className="title">Configuration</h3>
            </div>
            <div className="config-row">
              <span className="label">Provider</span>
              <span className="value">{provider}</span>
            </div>
            <div className="config-row">
              <span className="label">Response Model</span>
              <span className="value">{responseModel}</span>
            </div>
            <div className="config-row">
              <span className="label">Memory</span>
              <span className={`pill ${memoryEnabled ? 'on' : ''}`}>
                {memoryEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>

          {agentTools.length > 0 && (
            <div className="card">
              <div className="card-head">
                <h3 className="title">
                  <Wrench size={20} />
                  Tools & Integrations
                </h3>
              </div>
              <div className="chips">
                {agentTools.map((data, index) => (
                  <span key={data?.tool_id || index}>{data?.tool_name}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </ProfilePage>
  );
};

export default AgentProfile;
